import type { RouteLocationNormalizedLoaded, RouteLocationRaw } from 'vue-router'
import type { ClubHomeEvent } from '@/api'
import { isDevelopmentProgram } from '@/composables/useSeasonEvents'
import { eventViewAccess } from '@/stores/eventView'

type EventRoute = Pick<RouteLocationNormalizedLoaded, 'path' | 'params' | 'query' | 'meta'>
const suffixFor = (route: EventRoute) => route.path.replace(/^\/clubhouse\/[^/]+\/season\/[^/]+\/tryout\/[^/]+/, '')
export function canSwitchToEvent(route: EventRoute, event: ClubHomeEvent, section?: string) {
  const access = eventViewAccess(event.roles)
  if (route.meta.eventView === 'athlete') return access.athlete
  const suffix = suffixFor(route)
  if (section === 'athletes' || suffix === '/athletes' || (!suffix && route.query.section === 'athletes')) {
    return ['owner', 'admin', 'selector', 'headcoach', 'assistantcoach'].some(role => event.roles?.[role as keyof NonNullable<ClubHomeEvent['roles']>] === true)
  }
  if (suffix === '/import-athletes') return event.roles?.admin === true || event.roles?.owner === true
  if (suffix.startsWith('/episodes') || suffix === '/program-review') return access.staff && isDevelopmentProgram(event.eventType)
  if (route.meta.eventView === 'shared' || !suffix) return access.staff || access.athlete
  return access.staff
}

export function eventSwitchDestination(route: EventRoute, event: ClubHomeEvent, options: { section?: string; fullscreen?: boolean } = {}): RouteLocationRaw {
  const base = `/clubhouse/${encodeURIComponent(String(route.params.clubId))}/season/${encodeURIComponent(String(route.params.seasonId))}/tryout/${encodeURIComponent(event.id)}`
  let suffix = suffixFor(route)
  const query: Record<string, string> = {}
  // Only carry UI choices, never record IDs, search text, or an old return URL.
  for (const key of ['tab', 'section', 'view', 'workspace', 'sort', 'order']) {
    const value = route.query[key]
    if (typeof value === 'string') query[key] = value
  }
  if (/^\/sessions\/[^/]+\/planner$/.test(suffix)) { suffix = '/session-planner'; delete query.tab }
  if (suffix.startsWith('/episodes/')) { suffix = '/episodes'; delete query.tab }
  if (suffix === '/setup' || suffix === '/program-settings') {
    const next = isDevelopmentProgram(event.eventType) ? '/program-settings' : '/setup'
    if (next !== suffix) delete query.tab
    suffix = next
  }
  if (options.section) { suffix = ''; query.section = options.section; query.view = 'staff' }
  if (!suffix && query.section === 'athletes' && (options.fullscreen || route.query.fullscreen === '1')) query.fullscreen = '1'
  if (!suffix && !eventViewAccess(event.roles).staff) { query.view = 'athlete'; delete query.section; delete query.fullscreen }
  if (suffix) { delete query.section; delete query.view }
  return { path: `${base}${suffix}`, query }
}
